import React, { useEffect, useState } from 'react';
import { MdClose, MdSearch } from 'react-icons/md';
import toast from 'react-hot-toast';

const keyOf = (a) => `${a.role}:${a.userId}`;

// Admin popup for assigning teachers / HR to a PD or AI session. `service` is
// pdSessionService or aiSessionService; `onSaved` gets the updated session.
const AssignSessionModal = ({ isOpen, session, service, onClose, onSaved }) => {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !session) return;
    setSelected(session.assignedTo || []);
    setSearch('');
    const fetchPeople = async () => {
      try {
        setLoading(true);
        const response = await service.listAssignees();
        if (response.data.success) {
          setPeople(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching teachers / HR:', error);
        toast.error('Failed to load teachers / HR');
      } finally {
        setLoading(false);
      }
    };
    fetchPeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, session?._id]);

  if (!isOpen || !session) return null;

  const isSelected = (person) =>
    selected.some((a) => keyOf(a) === keyOf(person));

  const toggle = (person) => {
    setSelected((prev) =>
      isSelected(person)
        ? prev.filter((a) => keyOf(a) !== keyOf(person))
        : [...prev, person]
    );
  };

  const term = search.trim().toLowerCase();
  const filtered = people.filter(
    (p) =>
      !term ||
      p.name?.toLowerCase().includes(term) ||
      p.email?.toLowerCase().includes(term) ||
      p.label?.toLowerCase().includes(term)
  );

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await service.assignSession(
        session._id,
        selected.map(({ userId, role }) => ({ userId, role }))
      );
      if (response.data.success) {
        toast.success('Session assigned successfully');
        onSaved?.(response.data.data);
        onClose();
      }
    } catch (error) {
      console.error('Error assigning session:', error);
      toast.error(error?.response?.data?.message || 'Failed to assign session');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mx-4 flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl bg-white p-5 shadow-lg dark:bg-navy-800">
        <div className="mb-1 flex items-start justify-between gap-3">
          <h2 className="text-lg font-bold text-navy-700 dark:text-white">
            Assign "{session.title}"
          </h2>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10"
          >
            <MdClose className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-300">
          Assigned teachers / HR can see this session's students and take
          attendance. They can't edit or delete it.
        </p>

        <div className="relative mb-3">
          <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search teacher / HR by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 w-full rounded-xl border border-gray-200 bg-white/0 pl-10 pr-3 text-sm outline-none dark:!border-white/10 dark:text-white dark:placeholder:text-white/50"
          />
        </div>

        <div className="min-h-[160px] flex-1 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-brand-200 border-t-brand-500" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-center text-sm text-gray-500">
              No teachers / HR found.
            </p>
          ) : (
            filtered.map((person) => (
              <label
                key={keyOf(person)}
                className={`flex cursor-pointer items-center gap-3 border-b border-gray-100 p-3 text-sm last:border-b-0 dark:border-white/5 ${
                  isSelected(person)
                    ? 'bg-brand-50 dark:bg-white/5'
                    : 'hover:bg-gray-50 dark:hover:bg-white/5'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected(person)}
                  onChange={() => toggle(person)}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-navy-700 dark:text-white">
                    {person.name}
                  </p>
                  <p className="truncate text-xs text-gray-500">
                    {person.email}
                  </p>
                </div>
                <span className="whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                  {person.label}
                </span>
              </label>
            ))
          )}
        </div>

        {selected.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {selected.map((a) => (
              <span
                key={keyOf(a)}
                className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2.5 py-1 text-xs font-medium text-brand-600 dark:bg-navy-700 dark:text-brand-300"
              >
                {a.name} · {a.label}
                <button onClick={() => toggle(a)}>
                  <MdClose className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-gray-300 px-5 py-2 text-sm font-medium text-navy-700 dark:border-white/10 dark:text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="rounded-xl bg-brand-500 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Saving...' : `Save (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignSessionModal;
